import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { http } from '../../lib/api';
import { X, Calendar, Wrench, MapPin, Info } from 'lucide-react';

export default function AgendamientoForm({ agendamiento, onGuardar, onCancelar, tecnicos, clientes, mode = 'crear', isReadOnly = false }) {
  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    cliente_id: '',
    sucursal: '',
    equipos_ids: [],
    tipo_mantenimiento: 'preventivo',
    prioridad: 'media',
    estado: 'pendiente',
    fecha_programada: '',
    hora_inicio: '09:00',
    hora_fin: '12:00',
    ubicacion: '',
    tecnico_asignado: '',
    observaciones: '',
    // Recurrencia
    es_recurrente: false,
    frecuencia: 'semanal',
    intervalo_recurrencia: 1,
    dias_semana_recurrencia: [],
    fecha_fin_recurrencia: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [equiposCliente, setEquiposCliente] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [sucursales, setSucursales] = useState([]);

  const resolverCliente = (valor) => {
    if (!valor) return { cliente: null, id: '' };
    let cli = clientes?.find(c => c._id === valor || c.id === valor) || null;
    if (cli) return { cliente: cli, id: cli._id || cli.id };
    cli = clientes?.find(c => (c.nombre || '').toLowerCase() === String(valor).toLowerCase()) || null;
    if (cli) return { cliente: cli, id: cli._id || cli.id };
    return { cliente: null, id: '' };
  };

  useEffect(() => {
    if (agendamiento) cargarDatosAgendamiento();
  }, [agendamiento]);

  useEffect(() => {
    const run = async () => {
      if (!form.cliente_id) {
        setClienteSeleccionado(null);
        setEquiposCliente([]);
        setSucursales([]);
        setForm(prev => ({ ...prev, equipos_ids: [] }));
        return;
      }

      const { cliente, id: idNormalizado } = resolverCliente(form.cliente_id);
      const idConsulta = idNormalizado || form.cliente_id;
      if (idNormalizado && idNormalizado !== form.cliente_id) {
        setForm(prev => ({ ...prev, cliente_id: idNormalizado }));
      }
      setClienteSeleccionado(cliente);

      // 1) Cargar equipos primero y usar el resultado para fallback de sucursales
      const equiposList = await cargarEquiposCliente(idConsulta);

      // 2) Intentar obtener sucursales desde backend
      let lista = [];
      try {
        const token = localStorage.getItem('token');
        const res = await http.get(`/clientes/${idConsulta}/sucursales`, { headers: { Authorization: `Bearer ${token}` } });
        const rawData = Array.isArray(res.data) ? res.data : [];
        // Asegurar que lista sea de strings (nombres), no objetos
        lista = rawData.map(item => {
          if (typeof item === 'object' && item !== null) return item.nombre || '';
          return String(item);
        }).filter(Boolean);
      } catch (e) {
        console.warn('⚠️ No se pudieron obtener sucursales desde backend:', e?.response?.status);
      }

      // 3) Fallbacks: cliente.sucursales y derivadas desde equipos cargados
      const cliRef = cliente || resolverCliente(form.cliente_id).cliente;
      if (cliRef?.sucursales?.length) {
        const sucursalesCliente = cliRef.sucursales.map(s => {
          if (typeof s === 'object' && s !== null) return s.nombre || '';
          return String(s);
        });
        lista = [...new Set([...(lista || []), ...sucursalesCliente.filter(Boolean)])];
      }
      if (!lista.length && equiposList?.length) {
        // Obtenemos sucursales desde equipos, pero ignoramos "Principal" si es la única
        // ya que suele ser un valor por defecto en la BD que no representa una sucursal real del cliente
        const derivadas = [...new Set(equiposList.map(eq => eq.sucursal).filter(s => s && s !== 'Principal'))];
        lista = derivadas;
      }

      // IMPORTANTE: Filtrar explícitamente "Principal" de la lista final
      // Esto previene que se seleccione automáticamente una sucursal fantasma que bloquea la vista de equipos
      lista = lista.filter(s => s !== 'Principal');
      
      setSucursales(lista);
      
      // Lógica de corrección de sucursal seleccionada
      if (lista.length === 0) {
        // Si no hay sucursales, limpiar selección para que se vean todos los equipos
        if (form.sucursal) setForm(prev => ({ ...prev, sucursal: '' }));
      }
      else if (lista.length === 1) {
        // Si hay una sola, la seleccionamos automáticamente
        if (form.sucursal !== lista[0]) {
           setForm(prev => ({ ...prev, sucursal: lista[0] }));
        }
      } else if (lista.length > 0) {
        // Si hay varias y la actual no está en la lista, limpiar
        if (form.sucursal && !lista.includes(form.sucursal)) {
           setForm(prev => ({ ...prev, sucursal: '' }));
        }
      } else {
        // Si no hay sucursales, limpiar para permitir ver todos los equipos
        if (form.sucursal) {
           setForm(prev => ({ ...prev, sucursal: '' }));
        }
      }
      
    };
    run();
  }, [form.cliente_id, clientes]);

  // Refiltrar equipos cuando cambia la sucursal (sin volver a calcular sucursales)
  useEffect(() => {
    if (!form.cliente_id) return;
    const { id } = resolverCliente(form.cliente_id);
    cargarEquiposCliente(id || form.cliente_id);
  }, [form.sucursal]);

  const cargarEquiposCliente = async (clienteIdParam) => {
    const clienteIdUsar = clienteIdParam || form.cliente_id;
    if (!clienteIdUsar) return;
    try {
      const response = await http.get('/equipos/');
      const todosEquipos = response.data || [];
      const cli = resolverCliente(clienteIdUsar).cliente || resolverCliente(form.cliente_id).cliente;
      const equiposDelCliente = todosEquipos.filter(equipo => {
        const coincidePorId = equipo.cliente_id === clienteIdUsar || equipo.cliente?._id === clienteIdUsar || equipo.cliente?.id === clienteIdUsar;
        const coincidePorNombre = cli && (equipo.cliente?.nombre === cli.nombre || (equipo.cliente_nombre && equipo.cliente_nombre === cli.nombre));
        return coincidePorId || coincidePorNombre;
      });
      const equiposFiltrados = form.sucursal ? equiposDelCliente.filter(e => e.sucursal === form.sucursal) : equiposDelCliente;
      setEquiposCliente(equiposFiltrados);
      return equiposFiltrados;
    } catch (error) {
      console.error('Error cargando equipos del cliente:', error);
      setEquiposCliente([]);
      return [];
    }
  };

  const cargarDatosAgendamiento = () => {
    const a = agendamiento;
    if (!a) return;
    let clienteId = '';
    if (a.cliente_id) clienteId = a.cliente_id;
    else if (a.cliente && a.cliente._id) clienteId = a.cliente._id;
    else if (a.cliente && a.cliente.id) clienteId = a.cliente.id;
    setForm({
      titulo: a.titulo || '',
      descripcion: a.descripcion || '',
      cliente_id: clienteId,
      sucursal: a.sucursal || '',
      equipos_ids: a.equipos?.map(e => e._id || e.id) || a.equipos_ids || [],
      tipo_mantenimiento: a.tipo_mantenimiento || 'preventivo',
      prioridad: a.prioridad || 'media',
      estado: a.estado || 'pendiente',
      fecha_programada: a.fecha_programada ? a.fecha_programada.split('T')[0] : '',
      hora_inicio: a.hora_inicio || '09:00',
      hora_fin: a.hora_fin || '12:00',
      ubicacion: a.ubicacion || '',
      tecnico_asignado: a.tecnico_asignado || '',
      observaciones: a.observaciones || '',
      es_recurrente: a.es_recurrente || false,
      frecuencia: a.frecuencia || 'semanal',
      intervalo_recurrencia: a.intervalo_recurrencia || 1,
      dias_semana_recurrencia: a.dias_semana_recurrencia || [],
      fecha_fin_recurrencia: a.fecha_fin_recurrencia ? a.fecha_fin_recurrencia.split('T')[0] : ''
    });
  };

  const toggleDiaSemana = (diaIndex) => {
    setForm(prev => {
      const current = prev.dias_semana_recurrencia || [];
      if (current.includes(diaIndex)) {
        return { ...prev, dias_semana_recurrencia: current.filter(d => d !== diaIndex) };
      } else {
        return { ...prev, dias_semana_recurrencia: [...current, diaIndex].sort() };
      }
    });
  };

  const validarFormulario = () => {
    const newErrors = {};
    if (!form.titulo.trim()) newErrors.titulo = 'El título es requerido';
    if (!form.cliente_id) newErrors.cliente_id = 'Debe seleccionar un cliente';
    if (!form.fecha_programada) newErrors.fecha_programada = 'La fecha es requerida';
    if (!form.hora_inicio) newErrors.hora_inicio = 'La hora de inicio es requerida';
    if (!form.hora_fin) newErrors.hora_fin = 'La hora de fin es requerida';
    if (form.hora_inicio >= form.hora_fin) newErrors.hora_fin = 'La hora de fin debe ser posterior a la hora de inicio';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;
    setLoading(true);
    try {
      const fechaCompleta = `${form.fecha_programada}T${form.hora_inicio}:00`;
      const dataToSend = {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim(),
        cliente_id: form.cliente_id,
        sucursal: form.sucursal || null,
        equipos_ids: form.equipos_ids,
        tipo_mantenimiento: form.tipo_mantenimiento,
        prioridad: form.prioridad,
        estado: form.estado,
        fecha_programada: fechaCompleta,
        hora_inicio: form.hora_inicio,
        hora_fin: form.hora_fin,
        ubicacion: form.ubicacion.trim(),
        tecnico_asignado: form.tecnico_asignado || null,
        observaciones: form.observaciones.trim(),
        es_recurrente: form.es_recurrente,
        frecuencia: form.es_recurrente ? form.frecuencia : null,
        intervalo_recurrencia: form.es_recurrente ? Number(form.intervalo_recurrencia) : 1,
        dias_semana_recurrencia: form.es_recurrente ? form.dias_semana_recurrencia : [],
        fecha_fin_recurrencia: form.es_recurrente && form.fecha_fin_recurrencia ? `${form.fecha_fin_recurrencia}T23:59:59` : null
      };
      const id = agendamiento?._id || agendamiento?.id;
      if (id) {
        await http.put(`/agendamientos/${id}`, dataToSend);
      } else {
        await http.post('/agendamientos/', dataToSend);
      }
      alert('Agendamiento guardado exitosamente');
      onGuardar();
    } catch (error) {
      console.error('Error guardando agendamiento:', error);
      let errorMessage = 'Error desconocido';
      if (error.response?.data?.detail) {
        errorMessage = Array.isArray(error.response.data.detail)
          ? error.response.data.detail.map(e => e.msg).join(', ')
          : error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert(`Error al guardar el agendamiento: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const toggleEquipo = (equipoId) => {
    if (isReadOnly) return;
    setForm(prev => ({
      ...prev,
      equipos_ids: prev.equipos_ids.includes(equipoId)
        ? prev.equipos_ids.filter(id => id !== equipoId)
        : [...prev.equipos_ids, equipoId]
    }));
  };

  const modalContent = (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200 border border-slate-200 flex flex-col">
        <div className="sticky top-0 z-10 flex justify-between items-center px-6 py-4 border-b border-slate-100 bg-white/80 backdrop-blur-md">
          <h2 className="text-xl font-bold flex items-center gap-3 text-slate-800">
            <div className={`p-2 rounded-lg ${isReadOnly ? 'bg-emerald-50 text-emerald-600' : mode === 'crear' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
              {isReadOnly ? <Calendar className="w-6 h-6" /> : mode === 'crear' ? <Calendar className="w-6 h-6" /> : <Wrench className="w-6 h-6" />}
            </div>
            {isReadOnly ? 'Detalles del Agendamiento' : agendamiento ? 'Editar Agendamiento' : 'Nuevo Agendamiento'}
          </h2>
          <button 
            onClick={onCancelar} 
            className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700">Título *</label>
              <input
                type="text"
                name="titulo"
                value={form.titulo}
                onChange={handleInputChange}
                readOnly={isReadOnly}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all ${errors.titulo ? 'border-red-500 bg-red-50' : 'border-slate-200'} ${isReadOnly ? 'bg-slate-50 text-slate-500' : ''}`}
                placeholder="Título del agendamiento"
              />
              {errors.titulo && <p className="text-red-500 text-xs mt-1 font-medium">{errors.titulo}</p>}
            </div>

            <div>
               <label className="block text-sm font-medium mb-1 text-slate-700">Prioridad</label>
               <select
                name="prioridad"
                value={form.prioridad}
                onChange={handleInputChange}
                disabled={isReadOnly}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all border-slate-200 ${isReadOnly ? 'bg-slate-50 text-slate-500' : ''}`}
               >
                 <option value="baja">Baja</option>
                 <option value="media">Media</option>
                 <option value="alta">Alta</option>
                 <option value="critica">Crítica</option>
               </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700">Cliente *</label>
              <select
                name="cliente_id"
                value={form.cliente_id}
                onChange={handleInputChange}
                disabled={isReadOnly}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all ${errors.cliente_id ? 'border-red-500 bg-red-50' : 'border-slate-200'} ${isReadOnly ? 'bg-slate-50 text-slate-500' : ''}`}
              >
                <option value="">Seleccionar cliente</option>
                {clientes?.map(cliente => (
                  <option key={cliente._id || cliente.id} value={cliente._id || cliente.id}>{cliente.nombre}</option>
                ))}
              </select>
              {errors.cliente_id && <p className="text-red-500 text-xs mt-1 font-medium">{errors.cliente_id}</p>}
            </div>
          </div>

          {clienteSeleccionado && sucursales && sucursales.length > 0 && (
            <div className="animate-in slide-in-from-top-2 duration-200">
              <label className="block text-sm font-medium mb-1 text-slate-700">Sucursal {sucursales.length > 1 ? '*' : ''}</label>
              <select
                name="sucursal"
                value={form.sucursal}
                onChange={handleInputChange}
                disabled={isReadOnly}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all ${errors.sucursal ? 'border-red-500 bg-red-50' : 'border-slate-200'} ${isReadOnly ? 'bg-slate-50 text-slate-500' : ''}`}
              >
                <option value="">{sucursales.length === 1 ? `Sucursal única: ${sucursales[0]}` : 'Seleccionar sucursal'}</option>
                {sucursales.map(s => (<option key={s} value={s}>{s}</option>))}
              </select>
              {errors.sucursal && <p className="text-red-500 text-xs mt-1 font-medium">{errors.sucursal}</p>}
              {sucursales.length === 1 && !form.sucursal && (<p className="text-emerald-600 text-xs mt-1 font-medium">Este cliente tiene una sola sucursal. Selecciónala para ver los equipos.</p>)}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Descripción</label>
            <textarea
              name="descripcion"
              value={form.descripcion}
              onChange={handleInputChange}
              readOnly={isReadOnly}
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${isReadOnly ? 'bg-gray-50' : 'border-gray-300'}`}
              placeholder="Descripción detallada del mantenimiento"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Tipo de Mantenimiento</label>
              <select name="tipo_mantenimiento" value={form.tipo_mantenimiento} onChange={handleInputChange} disabled={isReadOnly} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${isReadOnly ? 'bg-gray-50' : 'border-gray-300'}`}>
                <option value="preventivo">Preventivo</option>
                <option value="correctivo">Correctivo</option>
                <option value="revision">Revisión</option>
                <option value="limpieza">Limpieza</option>
                <option value="actualizacion">Actualización</option>
                <option value="presupuesto">Presupuesto</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Estado</label>
              <select name="estado" value={form.estado} onChange={handleInputChange} disabled={isReadOnly} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${isReadOnly ? 'bg-gray-50' : 'border-gray-300'}`}>
                <option value="pendiente">Pendiente</option>
                <option value="confirmado">Confirmado</option>
                <option value="en_progreso">En Progreso</option>
                <option value="completado">Completado</option>
                <option value="cancelado">Cancelado</option>
                <option value="reagendado">Reagendado</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Fecha Programada *</label>
              <input type="date" name="fecha_programada" value={form.fecha_programada} onChange={handleInputChange} readOnly={isReadOnly} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${errors.fecha_programada ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'bg-gray-50' : ''}`} />
              {errors.fecha_programada && <p className="text-red-500 text-xs mt-1">{errors.fecha_programada}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Hora Inicio *</label>
              <input type="time" name="hora_inicio" value={form.hora_inicio} onChange={handleInputChange} readOnly={isReadOnly} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${errors.hora_inicio ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'bg-gray-50' : ''}`} />
              {errors.hora_inicio && <p className="text-red-500 text-xs mt-1">{errors.hora_inicio}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Hora Fin *</label>
              <input type="time" name="hora_fin" value={form.hora_fin} onChange={handleInputChange} readOnly={isReadOnly} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${errors.hora_fin ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'bg-gray-50' : ''}`} />
              {errors.hora_fin && <p className="text-red-500 text-xs mt-1">{errors.hora_fin}</p>}
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-2">
             <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input 
                  type="checkbox" 
                  name="es_recurrente" 
                  checked={form.es_recurrente} 
                  onChange={(e) => setForm(prev => ({ ...prev, es_recurrente: e.target.checked }))}
                  disabled={isReadOnly}
                  className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                />
                <span className="text-sm font-bold text-slate-700">Repetir agendamiento</span>
             </label>

             {form.es_recurrente && (
               <div className="animate-in slide-in-from-top-2 duration-200 space-y-4 pt-2">
                  <div className="flex flex-wrap gap-6 items-end">
                    <div>
                       <label className="block text-xs font-bold text-slate-500 mb-1">Repetir cada</label>
                       <div className="flex items-center gap-2">
                         <input 
                           type="number" 
                           name="intervalo_recurrencia" 
                           min="1" 
                           max="99" 
                           value={form.intervalo_recurrencia} 
                           onChange={handleInputChange}
                           disabled={isReadOnly}
                           className="w-16 px-2 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                         />
                         <select 
                           name="frecuencia" 
                           value={form.frecuencia} 
                           onChange={handleInputChange}
                           disabled={isReadOnly}
                           className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500"
                         >
                            <option value="diaria">Día(s)</option>
                            <option value="semanal">Semana(s)</option>
                            <option value="mensual">Mes(es)</option>
                            <option value="anual">Año(s)</option>
                         </select>
                       </div>
                    </div>
                    
                    <div className="flex-1 min-w-[200px]">
                       <label className="block text-xs font-bold text-slate-500 mb-1">Repetir hasta (fecha fin)</label>
                       <input 
                         type="date" 
                         name="fecha_fin_recurrencia" 
                         value={form.fecha_fin_recurrencia} 
                         onChange={handleInputChange}
                         disabled={isReadOnly}
                         min={form.fecha_programada}
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                       />
                    </div>
                  </div>

                  {form.frecuencia === 'semanal' && (
                    <div>
                       <label className="block text-xs font-bold text-slate-500 mb-2">Días de la semana</label>
                       <div className="flex gap-2 flex-wrap">
                          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d, i) => (
                             <button
                               key={d}
                               type="button"
                               disabled={isReadOnly}
                               onClick={() => toggleDiaSemana(i)}
                               className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                                 form.dias_semana_recurrencia?.includes(i) 
                                 ? 'bg-emerald-600 border-emerald-600 text-white shadow-md' 
                                 : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                               }`}
                             >
                               {d}
                             </button>
                          ))}
                       </div>
                    </div>
                  )}
                  
                  <div className="bg-emerald-50 text-emerald-800 text-xs p-3 rounded-lg flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>Se crearán múltiples agendamientos independientes basados en esta configuración.</p>
                  </div>
               </div>
             )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Ubicación</label>
              <input type="text" name="ubicacion" value={form.ubicacion} onChange={handleInputChange} readOnly={isReadOnly} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${isReadOnly ? 'bg-gray-50' : 'border-gray-300'}`} placeholder="Ubicación específica" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Técnico Asignado</label>
              <select name="tecnico_asignado" value={form.tecnico_asignado} onChange={handleInputChange} disabled={isReadOnly} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${isReadOnly ? 'bg-gray-50' : 'border-gray-300'}`}>
                <option value="">Seleccionar técnico</option>
                {tecnicos?.map(t => (<option key={t._id || t.id} value={t._id || t.id}>{t.nombre_completo || t.username}</option>))}
              </select>
            </div>
          </div>

          {/* Selección de Equipos */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Equipos a trabajar (opcional)</label>
              {form.cliente_id && (
                <span className="text-xs text-gray-500">
                  {equiposCliente.length > 0 ? `${equiposCliente.length} equipo${equiposCliente.length !== 1 ? 's' : ''}${form.sucursal ? ` en ${form.sucursal}` : ''}` : 'Buscando equipos...'}
                </span>
              )}
            </div>

            {!form.cliente_id ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <Wrench className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Selecciona un cliente para ver los equipos disponibles</p>
              </div>
            ) : !clienteSeleccionado ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-gray-500 text-sm">Cargando información del cliente...</p>
              </div>
            ) : equiposCliente.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <Wrench className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <p className="text-yellow-700 text-sm">{!form.sucursal ? 'No hay equipos registrados para este cliente' : `No hay equipos registrados en la sucursal "${form.sucursal}"`}</p>
                {!form.sucursal && (
                  <p className="text-yellow-600 text-xs mt-1">Este cliente no tiene sucursales configuradas o no se han asignado aún.</p>
                )}
                {form.sucursal && (
                  <p className="text-yellow-600 text-xs mt-1">Puedes crear el agendamiento sin equipos específicos</p>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">{form.sucursal ? <>Selecciona los equipos de <strong>{form.sucursal}</strong> en los que se realizará el mantenimiento:</> : <>Sin sucursal seleccionada: mostrando todos los equipos del cliente. Puedes filtrar por sucursal para acotar.</>}</p>
                  {sucursales?.length > 1 && (<div className="text-xs text-gray-500">Sucursales: {sucursales.join(', ')}</div>)}
                  {(!sucursales || sucursales.length === 0) && (
                    <div className="text-xs text-gray-500">Sin sucursales configuradas</div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto border rounded-lg p-4 bg-gray-50">
                  {equiposCliente.map(equipo => (
                    <label key={equipo._id} className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all duration-200 ${form.equipos_ids.includes(equipo._id) ? 'bg-emerald-50 border-emerald-300 shadow-sm' : 'bg-white border-gray-200 hover:bg-gray-100 hover:border-gray-300'} ${isReadOnly ? 'cursor-default' : ''}`}>
                      <input type="checkbox" checked={form.equipos_ids.includes(equipo._id)} onChange={() => toggleEquipo(equipo._id)} disabled={isReadOnly} className="mt-1 mr-3 text-emerald-600 focus:ring-emerald-500" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 text-sm">{equipo.nombre}</div>
                        <div className="text-gray-600 text-xs">{equipo.marca} {equipo.modelo}</div>
                        {equipo.numero_serie && (<div className="text-gray-500 text-xs mt-1">S/N: {equipo.numero_serie}</div>)}
                        {equipo.ubicacion && (<div className="text-gray-500 text-xs">📍 {equipo.ubicacion}</div>)}
                        {equipo.sucursal && (<div className="text-emerald-600 text-xs font-medium">{equipo.sucursal}</div>)}
                      </div>
                    </label>
                  ))}
                </div>
                {form.equipos_ids.length > 0 && (
                  <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-sm font-medium text-emerald-800">{form.equipos_ids.length} equipo{form.equipos_ids.length > 1 ? 's' : ''} seleccionado{form.equipos_ids.length > 1 ? 's' : ''}{form.sucursal ? ` en ${form.sucursal}` : ''}</span>
                    </div>
                    <div className="text-xs text-emerald-700 mt-1">{equiposCliente.filter(e => form.equipos_ids.includes(e._id)).map(e => e.nombre).join(', ')}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Observaciones</label>
            <textarea name="observaciones" value={form.observaciones} onChange={handleInputChange} readOnly={isReadOnly} rows={3} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${isReadOnly ? 'bg-gray-50' : 'border-gray-300'}`} placeholder="Observaciones adicionales" />
          </div>

          {!isReadOnly && (
            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 sticky bottom-0 bg-white pb-2">
              <button 
                type="button" 
                onClick={onCancelar} 
                className="px-6 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                disabled={loading} 
                className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg shadow-emerald-200 hover:shadow-xl active:scale-95 transition-all flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Guardando...
                  </>
                ) : (
                  agendamiento ? 'Guardar Cambios' : 'Crear Agendamiento'
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
